import { useCallback, useMemo, useRef, useState } from 'react';
import { Avatar, Paper, Stack, SxProps, Tooltip, Typography } from '@mui/material';
import { Role, useAssignZoneToUserMutation, WorkflowStages, ZonesForStructureDocument } from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useAllUsersForZones } from '@/hooks/useAllUsersForZones';
import { useSetAtom, useAtomValue } from 'jotai';
import * as state from '@/state';
import { avatarStyles, containerStyles, nameStyles } from './styles';
import { UserConfirmationDialog } from '@/components/ZonesManagementRevamped/AssigneeAvatar/UserConfirmationDialog';
import { AssigneeDropdown } from './AssigneeDropdown';
import { AssigneeList } from './AssigneeList';
import { useFeatureFlag } from '@/hooks';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';
import { abyssColors } from '@/theme/colors';

type Props = {
  profileImage?: string;
  assigneeName?: string;
  zoneId?: string
  details?: WorkflowStages
  sx?: SxProps;
  assingeFieldColor?: string;
};

export const AssigneeAvatar = ({
  assigneeName,
  profileImage,
  zoneId,
  details,
  sx,
  assingeFieldColor,
}: Props) => {
  const fallbackProfileImage = '/assets/fallback_assignee_profile.svg';
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [assignUserToZone] = useAssignZoneToUserMutation();
  const setZones = useSetAtom(state.zones);
  const scrollContainerReference = useRef<HTMLDivElement | null>(null);
  const structureId = useGetStructureId();
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogState, setDialogState] = useState<{ title: string; description: string }>({ title: '', description: '' });
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const allStages = useAtomValue(state.workflowStages);
  const zones = useAtomValue(state.zones);
  const isAitTaskLead = useFeatureFlag('ait-task-lead');

  const previousStage = useMemo(() => {
    if (!details || !allStages) return null;
    return allStages.find(stage => stage.orderInWorkflow === details.orderInWorkflow - 1);
  }, [allStages, details]);

  const nextStage = useMemo(() => {
    if (!details || !allStages) return null;
    return allStages.find(stage => stage.orderInWorkflow === details.orderInWorkflow + 1);
  }, [allStages, details]);

  // TODO: We need to improve how workflow stages work together:
  // 1. Support more than just "previous" and "next" stages
  // 2. Add rules about who can be assigned across different stages
  // 3. Connect with user roles to control who can assign users
  // 4. Show clearer messages when assignment conflicts happen
  // 5. Handle workflows that split into multiple paths
  // 6. Keep track of assignment history for reporting
  const adjacentStages = useMemo(() => ({
    previousStage,
    nextStage,
  }), [previousStage, nextStage]);

  const handleDialogClose = () => {
    setSelectedUserId(null);
  };

  const handleDialogContinue = () => {
    if (selectedUserId) {
      assign(selectedUserId);
      setSelectedUserId(null);
    }
  };

  const {
    users,
    isLoading,
    currentPage,
    hasMoreUsers,
    setCurrentPage,
    setShouldLoadMoreUsers,
    setHasMoreUsers,
  } = useAllUsersForZones({ searchTerm });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(!open);
  };

  const handleSearchFieldClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget.parentElement || e.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSelectUser = (userId: string) => {
    if (!zoneId || !userId) return;

    const userAssignments: { previous?: string; next?: string } = {};

    zones.forEach(zone => {
      const stageId = zone.workflowStatus?.workflowStageId;
      const assignedUserId = zone.workflowStatus?.assignedToUser?.id;

      if (assignedUserId !== userId) return;

      if (stageId === adjacentStages.previousStage?.id) {
        userAssignments.previous = adjacentStages.previousStage?.name;
      }

      if (stageId === adjacentStages.nextStage?.id) {
        userAssignments.next = adjacentStages.nextStage?.name;
      }
    });

    const { previous, next } = userAssignments;
    if (previous || next) {
      let stageDescription = '';
      if (previous && next) {
        stageDescription = `both "${previous}" and "${next}" workflow stages`;
      } else if (previous) {
        stageDescription = `"${previous}" workflow stage`;
      } else if (next) {
        stageDescription = `"${next}" workflow stage`;
      }

      setDialogState({
        title: 'User Already Assigned in Adjacent Stage',
        description: `This user is already assigned to a zone in the ${stageDescription}. Do you want to assign them to this zone as well?`,
      });
      assign(userId);
      return;
    }

    assign(userId);
  };

  const assign = async (userId: string) => {
    if (!zoneId) return
    await assignUserToZone({
      variables: {
        input: {
          zoneIds: [zoneId],
          assigneeId: userId,
          structureId,
        },
      },
      refetchQueries: [ZonesForStructureDocument]
    });
    handleClose();
    setZones((prev) => {
      return prev.map((zone) => {
        if (zone.id === zoneId) {
          const user = users?.find(user => user.id === userId);
          if (!user) return zone;
          const { id, name, email, picture, roleIds } = user
          return {
            ...zone,
            workflowStatus: {
              ...zone.workflowStatus!!,
              assignedToUser: { id, name, picture, email, roleIds: roleIds as Role[] }
            },
          };
        }
        return zone;
      })
    });
    setSearchTerm('');
  };

  const handleScroll = useCallback(() => {
    if (!scrollContainerReference.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerReference.current;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50;

    if (users && !users.length) {
      hasMoreUsers && setHasMoreUsers(false)
    }
    if (isNearBottom && hasMoreUsers) {
      setCurrentPage(currentPage && currentPage + 1);
      setShouldLoadMoreUsers(true);
    }

  }, [users, hasMoreUsers, setHasMoreUsers, setCurrentPage, currentPage, setShouldLoadMoreUsers]);

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          ...containerStyles,
          ...(assigneeName ? {} : { width: '130px' }),
          ...(open ? {
            backgroundColor: 'transparent',
            border: `1px solid ${abyssColors.primary[300]}`
          } : {}),
          ...sx
        }}
        onClick={handleClick}
      >
        {isAitTaskLead ? (
          <AssigneeDropdown
            searchTerm={searchTerm}
            assigneeName={assigneeName}
            profileImage={profileImage}
            fallbackProfileImage={fallbackProfileImage}
            open={open}
            handleSearchFieldClick={handleSearchFieldClick}
            setSearchTerm={setSearchTerm}
            assingeFieldColor={assingeFieldColor}
          />
        ) : (
          <>
            <Tooltip title={assigneeName || "Unassigned"} arrow>
              <Stack direction="row" alignItems="center" sx={{ cursor: 'pointer', width: '100%' }}>
                <Avatar sx={avatarStyles} alt="Assignee avatar" src={profileImage || fallbackProfileImage} />
                <Typography variant="body1" sx={{ ...nameStyles, color: assingeFieldColor }}>
                  {assigneeName || "Unassigned"}
                </Typography>
              </Stack>
            </Tooltip>
            {open ? (
              <ArrowDropDownIcon sx={{ color: assingeFieldColor }} />
            ) : (
              <ArrowDropDownIcon sx={{ color: assingeFieldColor }} />
            )}
          </>
        )}
      </Paper>

      <AssigneeList
        open={open}
        anchorEl={anchorEl}
        zoneId={zoneId}
        users={users}
        isLoading={isLoading}
        fallbackProfileImage={fallbackProfileImage}
        onClose={handleClose}
        onUserSelect={handleSelectUser}
        onScroll={handleScroll}
        scrollContainerReference={scrollContainerReference}
      />

      <UserConfirmationDialog
        title={dialogState.title}
        description={dialogState.description}
        handleDialogOpen={false}
        handleDialogClose={handleDialogClose}
        closeIcon={true}
        onContinue={handleDialogContinue}
      />
    </>
  );
};